/* ============================================================
   Whisk Hospitality — Brand index behaviour
   Type-to-filter over the rendered A–Z grid plus the letter jump bar.

   Filtering is client-side on purpose: every brand is already in the DOM, so there is
   nothing to fetch and the list stays usable with JavaScript disabled — the A–Z anchors
   are plain links and the grid renders in full.
   ============================================================ */

(function () {
  'use strict';

  var NORMALISE = /[^a-z0-9]+/g;

  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(NORMALISE, ' ')
      .trim();
  }

  function BrandIndex(root) {
    this.root = root;
    this.grid = root.querySelector('[data-brand-grid]');
    this.input = root.querySelector('[data-brand-search]');
    this.status = root.querySelector('[data-brand-status]');
    this.empty = root.querySelector('[data-brand-empty]');
    this.featured = root.querySelector('.wbi__featured');
    this.clearButtons = root.querySelectorAll('[data-brand-clear]');
    // Normalise each haystack once at init rather than per keystroke, so punctuation in a
    // brand name doesn't block a match — "fhe" finds "F.H.E", "blueseal" finds "Blue Seal".
    this.tiles = Array.prototype.slice
      .call(root.querySelectorAll('[data-brand-tile]'))
      .map(function (el) {
        var name = normalise(el.getAttribute('data-brand-name'));
        return {
          el: el,
          letter: el.getAttribute('data-brand-of-letter'),
          key: name + ' ' + name.replace(/ /g, '')
        };
      });
    this.letters = Array.prototype.slice.call(root.querySelectorAll('[data-brand-letter]'));
    this.alphaLinks = Array.prototype.slice.call(root.querySelectorAll('[data-brand-alpha]'));
    // The category index reuses this script, so the noun in the status line comes from the
    // markup rather than being fixed to "brand".
    this.one = root.getAttribute('data-index-noun') || 'brand';
    this.many = root.getAttribute('data-index-noun-plural') || 'brands';
    this.total = this.tiles.filter(function (tile) {
      return tile.letter;
    }).length;

    this.bind();
  }

  BrandIndex.prototype.bind = function () {
    var self = this;

    if (this.input) {
      this.input.addEventListener('input', function () {
        self.filter(self.input.value);
      });

      this.input.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && self.input.value !== '') {
          // Swallow the key so Escape clears the field rather than closing a parent
          // drawer or dialog while the shopper is mid-search.
          event.preventDefault();
          event.stopPropagation();
          self.clear();
        }
      });
    }

    Array.prototype.forEach.call(this.clearButtons, function (button) {
      button.addEventListener('click', function () {
        self.clear();
      });
    });

    Array.prototype.forEach.call(this.alphaLinks, function (link) {
      link.addEventListener('click', function (event) {
        var target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) return;
        event.preventDefault();
        var reduced =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
        // Keep the URL shareable and move focus for keyboard and screen-reader users.
        if (window.history && window.history.replaceState) {
          window.history.replaceState(null, '', link.getAttribute('href'));
        }
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  };

  BrandIndex.prototype.clear = function () {
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.filter('');
  };

  BrandIndex.prototype.filter = function (rawQuery) {
    var query = normalise(rawQuery);
    var terms = query ? query.split(' ') : [];
    var visibleLetters = {};
    var matches = 0;

    this.tiles.forEach(function (tile) {
      var hit =
        terms.length === 0 ||
        terms.every(function (term) {
          return tile.key.indexOf(term) !== -1;
        });

      tile.el.hidden = !hit;

      if (hit && tile.letter) {
        matches += 1;
        visibleLetters[tile.letter] = true;
      }
    });

    // A letter heading with no surviving tiles under it is noise — hide it, and mute the
    // matching entry in the jump bar so it can't scroll to an empty anchor.
    this.letters.forEach(function (heading) {
      heading.hidden = !visibleLetters[heading.getAttribute('data-brand-letter')];
    });

    this.alphaLinks.forEach(function (link) {
      var off = terms.length > 0 && !visibleLetters[link.getAttribute('data-brand-alpha')];
      link.setAttribute('aria-disabled', off ? 'true' : 'false');
    });

    if (this.featured) {
      // The featured row is a subset of the grid, so hide it entirely during a search
      // rather than showing the same brand twice above a single result.
      this.featured.hidden = terms.length > 0;
    }

    if (this.empty) this.empty.hidden = matches !== 0;

    Array.prototype.forEach.call(this.clearButtons, function (button) {
      if (button.classList.contains('wbi__search-clear')) button.hidden = terms.length === 0;
    });

    if (this.status) {
      if (terms.length === 0) {
        this.status.textContent = this.total + ' ' + (this.total === 1 ? this.one : this.many);
      } else {
        this.status.textContent =
          matches +
          (matches === 1 ? ' ' + this.one + ' matches ' : ' ' + this.many + ' match ') +
          '“' +
          rawQuery.trim() +
          '”';
      }
    }
  };

  // The theme's header group is sticky and changes height as the announcement bar scrolls
  // away, so the toolbar's offset is measured from the live element rather than hard-coded.
  function trackHeaderOffset(root) {
    // The header group holds more than one section — the announcement bar (static) and the
    // header itself (sticky) — so pick by what is actually pinned, not by document order.
    // Taking the lowest edge of the pinned ones also covers the header gaining a row.
    var sections = document.querySelectorAll('.shopify-section-group-header-group');
    var tools = root.querySelector('[data-brand-tools]');
    if (!sections.length || !tools) return;

    var queued = false;

    function stickyBottom() {
      var lowest = 0;
      Array.prototype.forEach.call(sections, function (section) {
        var position = window.getComputedStyle(section).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        var edge = section.getBoundingClientRect().bottom;
        if (edge > lowest) lowest = edge;
      });
      return lowest;
    }

    function apply() {
      queued = false;
      var bottom = Math.max(0, Math.round(stickyBottom()));
      root.style.setProperty('--wbi-top', bottom + 'px');
      // Only count the toolbar in the jump offset while it is actually pinned; below the
      // mobile breakpoint it scrolls away with the page.
      var pinned = window.getComputedStyle(tools).position === 'sticky';
      root.style.setProperty('--wbi-tools-height', pinned ? tools.offsetHeight + 'px' : '0px');
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(apply);
    }

    apply();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-brand-index]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.brandIndexReady === 'true') return;
      root.dataset.brandIndexReady = 'true';
      new BrandIndex(root);
      trackHeaderOffset(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
